import { useAuth } from "@clerk/clerk-react";
import { Box, Button, Chip, CircularProgress, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";

import { useAccountReviewState } from "features/Auth/Onboarding/shared/AccountReviewUpsell";
import { fetcherAuth } from "utils/api";

enum OnboardingReviewRequestStatus {
  pending = "pending",
  fulfilled = "fulfilled",
}

interface OnboardingReviewRequest {
  id: number;
  status: OnboardingReviewRequestStatus;
  isArchived: boolean;
  planName: string;
}

const RequestOnboardingReview = () => {
  const { getToken } = useAuth();
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { canSubmitReview } = useAccountReviewState();

  const [request, setRequest] = useState<OnboardingReviewRequest | null>(null);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    if (!currentOrg?.id || !canSubmitReview) {
      setFetchLoading(false);
      return;
    }
    const fetchRequest = async () => {
      setFetchLoading(true);
      try {
        const res = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/onboarding-review-requests`,
        );
        setRequest(res?.onboardingReviewRequest ?? null);
      } catch (error) {
        setErrorAlert(error);
      } finally {
        setFetchLoading(false);
      }
    };
    fetchRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, canSubmitReview]);

  if (!canSubmitReview) return null;

  const hasActiveRequest = request && !request.isArchived;
  const isFulfilled =
    hasActiveRequest &&
    request.status === OnboardingReviewRequestStatus.fulfilled;
  const isPending =
    hasActiveRequest &&
    request.status === OnboardingReviewRequestStatus.pending;

  const submitReview = async () => {
    if (!currentOrg?.id) return;
    setSubmitLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/onboarding-review-requests`,
        "POST",
      );
      setRequest(res?.onboardingReviewRequest ?? null);
      setAlert(
        "Review submitted! Our team will be in touch shortly.",
        "success",
      );
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const renderStatusChip = () => {
    if (isFulfilled) {
      return (
        <Chip
          label="✓ Review Complete"
          sx={{
            backgroundColor: "#00BA13",
            color: "white",
            fontWeight: 600,
            mb: 2,
          }}
        />
      );
    }
    if (isPending) {
      return (
        <Chip
          label="⏳ Review In Progress"
          sx={{
            backgroundColor: "yellow.main",
            color: "green.dark",
            fontWeight: 600,
            mb: 2,
          }}
        />
      );
    }
    return (
      <Box
        sx={{
          mb: 2,
          fontSize: 14,
          fontWeight: 600,
          backgroundColor: "white",
          color: "green.dark",
          px: 2,
          py: 1,
          borderRadius: 4,
          width: "max-content",
        }}
      >
        💎 Account Review
      </Box>
    );
  };

  const renderBody = () => {
    if (isFulfilled) {
      return (
        <Typography sx={{ color: "#ECEAE5" }}>
          Our Customer Success team has reviewed your account. Check your inbox
          for personalized feedback to boost your reply rate.
        </Typography>
      );
    }
    if (isPending) {
      return (
        <Typography sx={{ color: "#ECEAE5" }}>
          Our Customer Success team is reviewing your templates, follow-up
          sequences, and profile. You'll hear back from us soon.
        </Typography>
      );
    }
    return (
      <Typography sx={{ color: "#ECEAE5" }}>
        Have our Customer Success team review your templates, follow-up
        sequences, and profile so you're set up for success — and{" "}
        <Box component="span" sx={{ color: "yellow.main", fontWeight: 600 }}>
          boost your reply rate
        </Box>{" "}
        before you start pitching.
      </Typography>
    );
  };

  return (
    <Box
      sx={{
        p: 3,
        background:
          "linear-gradient(57.59deg, #151F16 -1.58%, #004537 98.35%), linear-gradient(180deg, rgba(236, 234, 229, 0) 0%, #ECEAE5 100%), #FFFFFF;",
        borderRadius: 4,
        mb: 1,
      }}
    >
      {renderStatusChip()}

      <Typography variant="h4" sx={{ color: "yellow.main", mb: 1 }}>
        {isFulfilled
          ? "Your account review is complete 🎉"
          : isPending
            ? "Your account is being reviewed 💎"
            : "Get a 1:1 account review from our Customer Success team 💎"}
      </Typography>

      <Box sx={{ mb: 2 }}>{renderBody()}</Box>

      {!hasActiveRequest && (
        <Button
          variant="outlined"
          fullWidth
          disabled={submitLoading || fetchLoading}
          onClick={submitReview}
          sx={{
            borderRadius: 2,
            py: 1,
            color: "white",
            borderColor: "white",
            "&:hover": {
              color: "yellow.main",
              borderColor: "yellow.main",
            },
          }}
        >
          {submitLoading ? (
            <CircularProgress size={20} sx={{ color: "white" }} />
          ) : (
            "Submit For Review"
          )}
        </Button>
      )}
    </Box>
  );
};

export default RequestOnboardingReview;
